import { ChangeEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { capitalize } from "lodash";
import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField } from "@mui/material";
import { AddressModel, BusinessAreaModel, EdiStandardModel, EmailModel, PartnerModel, PartnerStatus, PhoneModel, FunctionalReconciliationMode, UpsertPartnerInput, PartnerAkaModel, RequestResult, ExternalConnectionModel } from "../../gql-types.generated";
import { selectBusinessAreaList, selectEdiStandardList } from '../../features/EDIContainer/EDIContainerSlice';
import { selectExternalConnectionList } from "../../features/ExternalConnections/ExternalConnectionSlice";
import { fetchExternalConnectionList } from "../../features/ExternalConnections/ExternalConnectionActions";
import { BlankMenuItem, DialogGridColumn, DialogGridWithTopMargin } from "../../util/SharedStyles";
import AddEditDialog from "./AddEditDialog";
import DialogPartnerAkaList from "../lists/DialogPartnerAkaList";
import AddressFields from "../fields/AddressFields";
import PhoneFields from "../fields/PhoneFields";
import IsaQualifierField from "../fields/IsaQualifierField";
import LocaleField from "../fields/LocaleField";
import { getPrimaryAddressFromArray, getPrimaryEmailFromArray, getPrimaryPhoneFromArray } from '../../util/PrimaryFromModelArray';
import { ModelInputsSubmission } from "../../util/Constants";
import { convertAddressModelToUpsertAddressInput, convertEmailModelToUpsertEmailInput, convertPhoneModelToUpsertPhoneInput } from '../../util/Common';
import { isEmailValid, isURLValid } from "../../util/Validation";


interface TradingPartnerDialogProps {
    isOpen: boolean;
    partnerInfo?: PartnerModel;
    onClose: () => void;
    onSave: (
        partnerInput: UpsertPartnerInput,
        keepDialogOpen?: boolean
    ) => void;
    refreshPartnerData?: () => void;
    error?: Error | undefined;
    partnerSaveStatusResult?: RequestResult | undefined;
}

const statusOptions = [
    {
        value: PartnerStatus.Active,
        label: 'Active',
    },
    {
        value: PartnerStatus.Inactive,
        label: 'Inactive',
    },
    {
        value: PartnerStatus.New,
        label: 'New',
    },
];

const TradingPartnerDialog: React.FC<TradingPartnerDialogProps> = props => {
    const { isOpen, partnerInfo, onClose, onSave, refreshPartnerData, error, partnerSaveStatusResult } = props;
    const dispatch = useAppDispatch();
    
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [address, setAddress] = useState<AddressModel | undefined>(undefined);
    const [businessAreaId, setBusinessAreaId] = useState<string | undefined>(undefined);
    const [ediStandardId, setEdiStandardId] = useState<string | undefined>(undefined);
    const [email, setEmail] = useState<EmailModel | undefined>(undefined);
    const [qualifierCode, setQualifierCode] = useState('');
    const [name, setName] = useState('');
    const [partnerCode, setPartnerCode] = useState('');
    const [partnerId, setPartnerId] = useState<string | undefined>(undefined);
    const [phone, setPhone] = useState<PhoneModel | undefined>(undefined);
    const [status, setStatus] = useState<PartnerStatus>(PartnerStatus.Active);
    const [acknowledgementMode, setAcknowledgementMode] = useState<FunctionalReconciliationMode>(FunctionalReconciliationMode.Acknowledgement);
    const [acknowledgementTimeInterval, setAcknowledgementTimeInterval] = useState<number | undefined>(24);
    const [website, setWebsite] = useState('');
    const [internalPartnerCode, setInternalPartnerCode] = useState('');
    const [localeCode, setLocaleCode] = useState<string | undefined>(undefined);
    const [partnerAKAs, setPartnerAKAs] = useState<PartnerAkaModel[] | undefined>(undefined);
    const [emailInvalid, setEmailInvalid] = useState(false);
    const [emailErrorText, setEmailErrorText] = useState('');
    const [websiteErrorText, setWebsiteErrorText] = useState('');
    const [websiteInvalid, setWebsiteInvalid] = useState(false);
    const [connectionType, setConnectionType] = useState<string | undefined>(undefined);

    const businessAreas = useAppSelector(selectBusinessAreaList);
    const ediStandards = useAppSelector(selectEdiStandardList);
    const externalConnections = useAppSelector(selectExternalConnectionList);

    useEffect(() => {
            dispatch(fetchExternalConnectionList({isForPartner: true}));
    }, []);

    useEffect(() => {
        if (partnerInfo) {
            setFromTradingPartner();
        } else {
            setToDefaults();
        }
    }, [partnerInfo]);

    useEffect(() => {
        if (!isOpen) {
            setToDefaults();
            setSubmitted(false);
            setIsFormDirty(false);
        }
        else {
            setFromTradingPartner();
        }
    }, [isOpen]);

    useEffect(() => {
        // since the dialog can remain open with a preSave, set the submitted and dirty states to false
        // once the save status is received to stop the progress spinner from displaying
        if (partnerSaveStatusResult) {
            setSubmitted(false);
            setIsFormDirty(false);
        }
    }, [partnerSaveStatusResult]);

    const setToDefaults = () => {
        setName('');
        setBusinessAreaId(undefined);
        setEdiStandardId(undefined);
        setQualifierCode('');
        setPartnerCode('');
        setPartnerId(undefined);
        setStatus(PartnerStatus.Active);
        setAcknowledgementMode(FunctionalReconciliationMode.Acknowledgement);
        setAcknowledgementTimeInterval(24);
        setWebsite('');
        setInternalPartnerCode('');
        setAddressDefaults(undefined);
        setEmailDefaults(undefined);
        setPhoneDefaults(undefined);
        setPartnerAKAs(undefined);
        setLocaleCode(undefined);
        setConnectionType(undefined);
    };

    const setFromTradingPartner = () => {
        if (partnerInfo) {
            setName(partnerInfo.name as string || '');

            let parentId = partnerInfo.id;
            setPhoneOrDefault(parentId);
            setEmailOrDefault(parentId);
            setAddressOrDefault(parentId);

            if (partnerInfo.businessArea?.id?.length > 0) {
                setBusinessAreaId(partnerInfo.businessArea?.id);
            }
            if (partnerInfo.ediStandards && partnerInfo.ediStandards.length) {
                setEdiStandardId(partnerInfo.ediStandards[0]?.id);
            }
            if (partnerInfo.isaQualifierCode) {
                setQualifierCode(partnerInfo.isaQualifierCode);
            }
            if (partnerInfo.partnerCode) {
                setPartnerCode(partnerInfo.partnerCode);
            }
            if (partnerInfo.id) {
                setPartnerId(partnerInfo.id);
            }
            if (partnerInfo.statusCode) {
                setStatus(partnerInfo.statusCode);
            }
            if (partnerInfo.functionalReconciliationMode) {
                setAcknowledgementMode(partnerInfo.functionalReconciliationMode);
            } else {
                // set as default and enable save
                setAcknowledgementMode(FunctionalReconciliationMode.Acknowledgement);
                setIsFormDirty(true);
            }
            if (partnerInfo.acknowledgementTimeInterval) {
                setAcknowledgementTimeInterval(partnerInfo.acknowledgementTimeInterval);
            } else {
                setAcknowledgementTimeInterval(undefined);
            }
            if (partnerInfo.website) {
                validateAndSetURL(partnerInfo.website);
            }
            if (partnerInfo.internalPartnerCode) {
                setInternalPartnerCode(partnerInfo.internalPartnerCode);
            }
            if (partnerInfo.partnerAKAs) {
                setPartnerAKAs(partnerInfo.partnerAKAs as PartnerAkaModel[]);
            }
            setLocaleCode(partnerInfo?.localeCode as string);
            setConnectionType(partnerInfo?.externalConnectionType as string);
        }
    }

    const setAddressDefaults = (parentId: string | undefined) => {
        setAddress({
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            addressLine4: '',
            city: '',
            countryCode: '',
            postalCode: '',
            stateId: '',
            isPrimary: true,
            parentId: parentId,
        } as AddressModel);
    }

    const setEmailDefaults = (parentId: string | undefined) => {
        setEmail({
            email: '',
            parentId: parentId,
            isPrimary: true,
        } as EmailModel);
    }

    const setPhoneDefaults = (parentId: string | undefined) => {
        setPhone({
            phoneNumber: '',
            description: '',
            parentId: parentId,
            isPrimary: true,
        } as PhoneModel);
    }

    const setAddressOrDefault = (parentId: string | undefined) => {
        if (partnerInfo?.addresses) {
            const primary = getPrimaryAddressFromArray(partnerInfo.addresses as AddressModel[]);
            if (primary) {
                if (primary.isPrimary === undefined) {
                    // primary is not extensible, since it wasn't returned with this property, we can not add it
                    // make a clone and set the property before setting to the state
                    const newPrimary = Object.assign({}, primary, { isPrimary: true, parentId: parentId });
                    setAddress(newPrimary);
                } else {
                    setAddress(primary);
                }
            } else {
                setAddressDefaults(parentId);
            }
        } else {
            setAddressDefaults(parentId);
        }
    }

    const setPhoneOrDefault = (parentId: string | undefined) => {
        if (partnerInfo?.phones) {
            const primary = getPrimaryPhoneFromArray(partnerInfo.phones as PhoneModel[]);
            if (primary) {
                if (primary.isPrimary === undefined) {
                    // primary is not extensible, since it wasn't returned with this property, we can not add it
                    // make a clone and set the property before setting to the state
                    const newPrimary = Object.assign({}, primary, { isPrimary: true, parentId: parentId });
                    setPhone(newPrimary);
                } else {
                    setPhone(primary);
                }
            } else {
                setPhoneDefaults(parentId);
            }
        } else {
            setPhoneDefaults(parentId);
        }
    }

    const setEmailOrDefault = (parentId: string | undefined) => {
        if (partnerInfo?.emails) {
            const primary = getPrimaryEmailFromArray(partnerInfo.emails as EmailModel[]);
            if (primary) {
                if (primary.isPrimary === undefined) {
                    // primary is not extensible, since it wasn't returned with this property, we can not add it
                    // make a clone and set the property before setting to the state
                    const newPrimary = Object.assign({}, primary, { isPrimary: true, parentId: parentId });
                    validateAndSetEmail(newPrimary.email as string, newPrimary);
                } else {
                    validateAndSetEmail(primary.email as string, primary);
                }
            } else {
                setEmailDefaults(parentId);
            }
        } else {
            setEmailDefaults(parentId);
        }
    }

    const checkAddressModelForData = () => {
        if (address?.addressLine1 !== '' ||
            address?.addressLine1 !== '' ||
            address?.addressLine2 !== '' ||
            address?.addressLine3 !== '' ||
            address?.addressLine4 !== '' ||
            address?.city !== '' ||
            address?.countryCode !== '' ||
            address?.postalCode !== '' ||
            address?.stateId !== '') {
            return address;
        } else {
            return undefined;
        }
    }

    const getModelInputsData = () => {
        const emailVal = email?.email as string;
        const phoneVal = phone?.phoneNumber as string;
        let ret = { email: undefined, phone: undefined, address: checkAddressModelForData() } as ModelInputsSubmission;
        if (emailVal !== '') {
            ret.email = email;
        }
        if (phoneVal !== '') {
            ret.phone = phone;
        }
        return ret;
    }

    const validateAndSetEmail = (toValidate: string, partnerEmail?: EmailModel) => {
        if (toValidate || toValidate === "") {
            const isValid = isEmailValid(toValidate);
            setEmailInvalid(!isValid);
            if (isValid) {
                // if partnerEmail exists, we are in initial set, so don't want the form dirty
                if (partnerEmail === undefined) {
                    setIsFormDirty(true);
                }
                setEmailErrorText('');
            } else {
                setEmailErrorText('Invalid format.  Enter a valid email address');
            }
            // assign the passed in email (initial set only), or the stored email
            let currEmail = partnerEmail || email;
            if (currEmail) {
                const justMail = { email: toValidate };
                let newMail = {};
                Object.assign(newMail, currEmail, justMail);
                setEmail(newMail as EmailModel);
            } else {
                setEmail({
                    email: toValidate,
                    isPrimary: true,
                    parentId: partnerId,
                } as EmailModel);
            }
        }
    };
    const validateAndSetURL = (toValidate: string) => {
        if (toValidate || toValidate === "") {
            const isValid = isURLValid(toValidate);
            setWebsiteInvalid(!isValid);
            if (isValid) {
                // only set dirty if value is different than initial
                if (toValidate !== partnerInfo?.website) {
                    setIsFormDirty(true);
                }
                setWebsiteErrorText('');
            } else {
                setWebsiteErrorText('Invalid format.  Enter a valid URL.');
            }
            setWebsite(toValidate);
        }
    }

    const isFormValid = () => {
        // Check required fields first
        const requiredCheck = (
            isFormDirty && 
            name?.trim().length > 0 && 
            qualifierCode?.trim().length > 0 && 
            partnerCode?.trim().length > 0
        );
        
        if (!requiredCheck) {
            return false;
        }

        // If phone number exists, phone type must also exist
        const phoneVal = phone?.phoneNumber as string;
        if (phoneVal && phoneVal.trim().length > 0) {
            const phoneTypeVal = phone?.phoneTypeCode as string;
            if (!phoneTypeVal || phoneTypeVal.trim().length === 0) {
                return false;
            }
        }

        // Validate email if provided
        if (email && email.email && emailInvalid) {
            return false;
        }

        // Validate website if provided
        if (website && websiteInvalid) {
            return false;
        }
        
        // Validate acknowledgement time interval when mode is Acknowledgement
        if (acknowledgementMode === FunctionalReconciliationMode.Acknowledgement) {
            if (acknowledgementTimeInterval === undefined || 
                acknowledgementTimeInterval < 1 || 
                acknowledgementTimeInterval > 24) {
                return false;
            }
        }
        
        return true;
    };
    const onError = () => {
        setSubmitted(false);
    };
    const submitForm = (isPresave?: boolean) => {
        if (isFormValid()) {
            const models = getModelInputsData();
            setSubmitted(true);
            let addressInput = convertAddressModelToUpsertAddressInput(models.address);
            const addresses = addressInput ? [addressInput] : undefined;
            let emailInput = convertEmailModelToUpsertEmailInput(models.email);
            const emails = emailInput ? [emailInput] : undefined;
            let phoneInput = convertPhoneModelToUpsertPhoneInput(models.phone);
            const phones = phoneInput ? [phoneInput] : undefined;
            const ediStandardIds = ediStandardId ? [ediStandardId] : undefined;
            onSave(
                {
                    isaQualifierCode: qualifierCode,
                    addresses: addresses,
                    businessAreaId: businessAreaId,
                    ediStandardIds: ediStandardIds,
                    emails: emails,
                    id: partnerId,
                    internalPartnerCode: internalPartnerCode,
                    name: name,
                    partnerCode: partnerCode,
                    phones: phones,
                    statusCode: status,
                    website: website,
                    functionalReconciliationMode: acknowledgementMode,
                    acknowledgementTimeInterval: acknowledgementTimeInterval,
                    localeCode: localeCode,
                    externalConnectionType: connectionType
                },
                isPresave
            );
        }
    };

    const preSavePartnerData = () => {
        // submit form with isPresave = true to save the data without closing the dialog
        submitForm(true);  
      };

    const getBuisnessAreaOptions = () => {
        if (businessAreas && businessAreas.length) {
            return (
                businessAreas.map((businessArea: BusinessAreaModel) => (
                    <MenuItem
                        key={businessArea.id}
                        value={businessArea.id}
                    >
                        {businessArea.name}
                    </MenuItem>
                ))
            );
        }
        return null;
    };

    const getEdiStandardOptions = () => {
        if (ediStandards && ediStandards.length) {
            return (
                ediStandards.map((ediStand: EdiStandardModel) => (
                    <MenuItem
                        key={ediStand.id}
                        value={ediStand.id}
                    >
                        {ediStand.name}
                    </MenuItem>
                ))
            );
        }
    };

    const getExternalConnectionOptions = () => {
        if (externalConnections && externalConnections.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
            const dynamicItems = externalConnections.map((connection: ExternalConnectionModel) => (
                <MenuItem
                    key={connection.externalConnectionType}
                    value={connection.externalConnectionType}
                >
                    {connection.externalConnectionType}
                </MenuItem>
            ));
            items.push(blankItem);
            items.push(...dynamicItems);
            return items;
        }
        return null;
    };

    const acknowledgementModes = Object.values(FunctionalReconciliationMode);

    const getAcknowledgementModeOptions = () => {
        if (acknowledgementModes && acknowledgementModes.length) {
            let items = [];
            const mappedItems = (
                acknowledgementModes.map((mode: FunctionalReconciliationMode) => (
                    <MenuItem
                        key={mode}
                        value={mode}
                    >
                        {mode === FunctionalReconciliationMode.Na ? 'NA' : capitalize(mode)}
                    </MenuItem>
                ))
            );
            items.push(...mappedItems);
            return items;
        }
        return null;
    };
    
    // only displaying partnerAKA list when already have a partner
    // since the partnerId is needed to save a partnerAKA
    const getPartnerAkaList = () => {
        return (
            <DialogPartnerAkaList
                parentId={partnerId ?? ''}
                partnerAkas={partnerAKAs}
                isLoading={submitted}
                isPartnerFormDirty={isFormDirty}
                refreshPartnerData={refreshPartnerData}
                preSavePartnerData={preSavePartnerData}
            />
        );
    };

    const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setName(event.target.value);
    };
    const onPhoneNumberChange = (phoneModelObj: PhoneModel) => {
        setIsFormDirty(true);
        setPhone(phoneModelObj);
    };
    const onWebsiteChange = (event: ChangeEvent<HTMLInputElement>) => {
        validateAndSetURL(event.target.value);
    };
    const onInternalPartnerCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setInternalPartnerCode(event.target.value);
    };
    const onQualifierCodeChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        setQualifierCode(event.target.value ?? '');
    };
    const onPartnerCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setPartnerCode(event.target.value);
    };
    const onLocaleChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value && event.target.value.length > 0) {
            setLocaleCode(event.target.value);
        } else {
            setLocaleCode(undefined);
        }
    };
    const onAddressChange = (address: AddressModel) => {
        setIsFormDirty(true);
        setAddress(address);
    };
    const onBusinessAreaChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setBusinessAreaId(event.target.value);
        } else {
            setBusinessAreaId(undefined);
        }
    };
    const onEdiStandardChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setEdiStandardId(event.target.value);
        } else {
            setEdiStandardId(undefined);
        }
    };
    const onExternalConnectionChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setConnectionType(event.target.value);
        } else {
            setConnectionType(undefined);
        }
    };
    const onStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        const statusChangeValue = event.target.value || PartnerStatus.New;
        setStatus(statusChangeValue as PartnerStatus);
    };
    const onAcknowledgementModeChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        const newMode = event.target.value as FunctionalReconciliationMode;
        
        if (newMode === FunctionalReconciliationMode.Acknowledgement) {
            setAcknowledgementMode(newMode);
            setAcknowledgementTimeInterval(24);
            
        } else {
            setAcknowledgementMode(FunctionalReconciliationMode.Na);
            setAcknowledgementTimeInterval(undefined);
        }
    };
    
    const onAcknowledgementTimeIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        
        // Truncate to max 2 digits
        if (value.length > 2) {
            value = value.substring(0, 2);
        }
        
        const newNumValue = value === '' ? undefined : parseInt(value, 10);
        
        // Only set dirty if the value actually changed
        if (newNumValue !== acknowledgementTimeInterval) {
            setIsFormDirty(true);
        }
        
        setAcknowledgementTimeInterval(newNumValue);
    };

    const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        validateAndSetEmail(event.target.value);
    };

    const nameProps = {
        'aria-label': 'name',
        'maxLength': 255,
    };
    const websiteProps = {
        'aria-label': 'website',
        'maxLength': 255,
    };
    const internalPartnerCodeProps = {
        'aria-label': 'internal partner code',
        'maxLength': 25,
    };
    const businessAreaProps = {
        'aria-label': 'business area',
    };
    const ediStandardProps = {
        'aria-label': 'EDI Standard',
    };
    const externalConnectionProps = {
        'aria-label': 'external connection type',
    };
    const emailProps = {
        'aria-label': 'email',
        'maxLength': 255,
    };
    const partnerCodeProps = {
        'aria-label': 'partner code',
        'maxLength': 50,
    };
    const modeProps = {
        'aria-label': 'functional acknowledgement mode',
    };
    const acknowledgementTimeIntervalProps = {
        'aria-label': 'acknowledgement time interval hours',
        'min': 1,
        'max': 24,
    };

    return (
        <AddEditDialog
            isOpen={isOpen}
            isSubmitted={submitted}
            id={partnerId}
            entityName="Trading Partner"
            maxWidth='md'
            onClose={onClose}
            onSave={submitForm}
            validate={isFormValid}
            onError={onError}
            error={error}
        >
            <Grid container spacing={2} item xs={12}>
                <DialogGridWithTopMargin container item columns={{ xs: 1, sm: 2 }} spacing={2} xs={12}>
                    <DialogGridColumn id="detailsColumn" container item xs={12} sm={6}>
                        <Grid item xs={12}>
                            <Stack spacing={2}>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-trading-partner-name"
                                        fullWidth
                                        disabled={submitted}
                                        autoFocus
                                        value={name}
                                        label="Name"
                                        inputProps={nameProps}
                                        onChange={onNameChange}
                                        autoComplete="off"
                                        required
                                        data-cy="dialog-trading-partner-name"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-trading-partner-ediStandard-label">EDI Standard</InputLabel>
                                        <Select
                                            labelId="dialog-trading-partner-ediStandard-label"
                                            aria-labelledby="dialog-trading-partner-ediStandard-label"
                                            value={ediStandardId ?? ''}
                                            onChange={onEdiStandardChange}
                                            disabled={submitted}
                                            MenuProps={ediStandardProps}
                                            data-cy="dialog-trading-partner-ediStandard"
                                        >
                                            {getEdiStandardOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-trading-partner-internal-partner-code"
                                        fullWidth
                                        disabled={submitted}
                                        autoFocus
                                        value={internalPartnerCode}
                                        label="Internal Partner Code (auto-generated if not provided)"
                                        inputProps={internalPartnerCodeProps}
                                        onChange={onInternalPartnerCodeChange}
                                        autoComplete="off"
                                        data-cy="dialog-trading-partner-internal-partner-code"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-trading-partner-website"
                                        fullWidth
                                        error={websiteInvalid}
                                        disabled={submitted}
                                        helperText={websiteErrorText}
                                        value={website}
                                        label="Website"
                                        inputProps={websiteProps}
                                        onChange={onWebsiteChange}
                                        autoComplete="off"
                                        data-cy="dialog-trading-partner-website"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-trading-partner-status"
                                        fullWidth
                                        onChange={onStatusChange}
                                        defaultValue={PartnerStatus.Active}
                                        disabled={submitted}
                                        inputProps={{ 'aria-label': 'status' }}
                                        label="Status"
                                        value={status}
                                        data-cy="dialog-trading-partner-status"
                                        variant="standard"
                                        select
                                    >
                                        {statusOptions.map(statusValue => (
                                            <MenuItem key={statusValue.value} value={statusValue.value}>
                                                {statusValue.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-trading-partner-businessarea-label">Business Area</InputLabel>
                                        <Select
                                            labelId="dialog-trading-partner-businessarea-label"
                                            aria-labelledby="dialog-trading-partner-businessarea-label"
                                            value={businessAreaId ?? ''}
                                            onChange={onBusinessAreaChange}
                                            disabled={submitted}
                                            MenuProps={businessAreaProps}
                                            data-cy="dialog-trading-partner-businessarea"
                                        >
                                            {getBuisnessAreaOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-trading-partner-partnerCode"
                                        fullWidth
                                        disabled={submitted}
                                        value={partnerCode}
                                        label="Partner Code"
                                        inputProps={partnerCodeProps}
                                        onChange={onPartnerCodeChange}
                                        autoComplete="off"
                                        required
                                        data-cy="dialog-trading-partner-partnerCode"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <IsaQualifierField
                                        disabled={submitted}
                                        value={qualifierCode}
                                        onChange={onQualifierCodeChange}
                                        required
                                        data-cy="dialog-trading-partner-qualifier"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-trading-partner-email"
                                        fullWidth
                                        error={emailInvalid}
                                        helperText={emailErrorText}
                                        disabled={submitted}
                                        value={email?.email}
                                        label="Email"
                                        inputProps={emailProps}
                                        onChange={onEmailChange}
                                        autoComplete="off"
                                        data-cy="dialog-trading-partner-email"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-trading-partner-acknowledgement-mode-label">Functional Acknowledgement Mode</InputLabel>
                                        <Select
                                            labelId="dialog-trading-partner-acknowledgement-mode-label"
                                            aria-labelledby="dialog-trading-partner-acknowledgement-mode-label"
                                            value={acknowledgementMode ?? ""}
                                            onChange={onAcknowledgementModeChange}
                                            disabled={submitted}
                                            MenuProps={modeProps}
                                            data-cy="dialog-trading-partner-acknowledgement-mode"
                                        >
                                            {getAcknowledgementModeOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {acknowledgementMode === FunctionalReconciliationMode.Acknowledgement && (
                                    <Grid item >
                                        <TextField
                                            itemID="dialog-trading-partner-acknowledgement-time-interval"
                                            fullWidth
                                            disabled={submitted}
                                            value={acknowledgementTimeInterval ?? ''}
                                            label="Acknowledgement Time Interval (Hours)"
                                            inputProps={acknowledgementTimeIntervalProps}
                                            onChange={onAcknowledgementTimeIntervalChange}
                                            autoComplete="off"
                                            required
                                            type="number"
                                            data-cy="dialog-trading-partner-acknowledgement-time-interval"
                                            variant="standard"
                                            error={acknowledgementMode === FunctionalReconciliationMode.Acknowledgement && (acknowledgementTimeInterval === undefined || acknowledgementTimeInterval < 1 || acknowledgementTimeInterval > 24)}
                                            helperText={acknowledgementMode === FunctionalReconciliationMode.Acknowledgement && (acknowledgementTimeInterval === undefined || acknowledgementTimeInterval < 1 || acknowledgementTimeInterval > 24) ? 'Must be between 1 and 24 hours' : ''}
                                        />
                                    </Grid>
                                )}

                            </Stack>
                        </Grid>
                    </DialogGridColumn>
                    <DialogGridColumn id="addressColumn" container direction={"row"} item xs={12} sm={6}>
                        <Grid item xs={12}>
                            <AddressFields
                                parentId={partnerId}
                                address={address}
                                onChange={onAddressChange}
                                disabled={submitted}
                                data-cy="dialog-trading-partner-address-fields"
                            />
                        </Grid>
                        <DialogGridWithTopMargin item xs={12}>
                            <PhoneFields
                                phone={phone}
                                parentId={partnerId}
                                disabled={submitted}
                                onChange={onPhoneNumberChange}
                                data-cy="dialog-trading-partner-phone"
                            />
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <LocaleField
                                disabled={submitted}
                                value={localeCode ?? ''}
                                onChange={onLocaleChange}
                                data-cy="dialog-trading-partner-locale"
                            />
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel id="dialog-trading-partner-connection-label">External Connection Type</InputLabel>
                                <Select
                                    labelId="dialog-trading-partner-connection-label"
                                    aria-labelledby="dialog-trading-partner-connection-label"
                                    value={connectionType ?? ""}
                                    onChange={onExternalConnectionChange}
                                    disabled={submitted}
                                    MenuProps={externalConnectionProps}
                                    data-cy="dialog-trading-partner-connection"
                                >
                                    {getExternalConnectionOptions()}
                                </Select>
                            </FormControl>
                        </DialogGridWithTopMargin>
                    </DialogGridColumn>
                </DialogGridWithTopMargin>
                {partnerInfo?.id &&
                    <Grid item xs={12}>
                        {getPartnerAkaList()}
                    </Grid>
                }
            </Grid>
        </AddEditDialog>
    );
};

export default TradingPartnerDialog;
